import React, { useCallback, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const COPIED_FEEDBACK_MS = 2500;

const MfaRecoveryCodes = ({
  recoveryCodes,
  centered = false,
  onAcknowledge,
}: {
  recoveryCodes: string[];
  centered?: boolean;
  onAcknowledge: () => void;
}) => {
  const [acknowledged, setAcknowledged] = useState(false);
  const [copied, setCopied] = useState(false);

  const copyRecoveryCodes = useCallback(() => {
    if (!('clipboard' in navigator)) return;

    navigator.clipboard.writeText(recoveryCodes.join('\n')).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
    });
  }, [recoveryCodes, setCopied]);

  const toggleAcknowledged = useCallback((
    _event: React.SyntheticEvent<Element, Event>,
    checked: boolean,
  ) => {
    setAcknowledged(checked);
  }, [setAcknowledged]);

  return (
    <Box textAlign={centered ? 'center' : undefined}>
      <Typography variant="h6" gutterBottom>
        Save your recovery codes
      </Typography>
      <Alert severity="warning" sx={{ marginBottom: 2 }}>
        These codes are shown once and cannot be retrieved again. Store them somewhere safe —
        each one lets you sign in if you lose access to your authenticator app.
      </Alert>
      <Paper
        variant="outlined"
        sx={{
          padding: 2,
          marginBottom: 2,
          width: 'fit-content',
          maxWidth: '100%',
          marginX: centered ? 'auto' : 0,
        }}
      >
        <Box display="flex" alignItems="flex-start" gap={2}>
          <Box
            display="grid"
            gridTemplateColumns={{ xs: '1fr', sm: 'max-content max-content' }}
            columnGap={4}
            rowGap={1}
            fontFamily="monospace"
          >
            {recoveryCodes.map((recoveryCode) => (
              <Typography key={recoveryCode} variant="body1" fontFamily="monospace">
                {recoveryCode}
              </Typography>
            ))}
          </Box>
          <Tooltip arrow title={copied ? 'Copied!' : 'Copy all codes'}>
            <IconButton
              size="small"
              aria-label="Copy all codes"
              onClick={copyRecoveryCodes}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>
      <FormControlLabel
        control={<Checkbox checked={acknowledged} />}
        onChange={toggleAcknowledged}
        label="I have saved my recovery codes"
      />
      <Box marginTop={2}>
        <Button
          variant="contained"
          onClick={onAcknowledge}
          disabled={!acknowledged}
        >
          Done
        </Button>
      </Box>
    </Box>
  );
};

export default MfaRecoveryCodes;
